/**
 * InlineSectionSeatPanel — Sprint P-A1
 *
 * Renders BELOW the venue overview map when a section is selected.
 * Contract:
 *   props.sectionName            — currently-selected section name
 *   props.sectionAvailability    — the availability row for this section
 *   props.zone                   — the pricing zone assigned (may be null)
 *   props.filteredSeats          — event_seats filtered to this section
 *   props.layout                 — venue layout doc (for VisualSeatMap)
 *   props.selectedSeats          — global selectedSeats ids
 *   props.onToggleSeat           — toggle handler
 *   props.totalTickets           — cart ticket total
 *   props.onChangeSection        — show change-section flow
 *   props.onBest4                — trigger Best 4 Together
 *   props.best4Busy              — bool loading state
 *   props.onContinueCheckout     — checkout CTA
 *   props.hideChangeSection      — hide the button (single-section events)
 *   props.canContinueCheckout    — enable/disable checkout button
 *
 * Sprint R1 additions:
 *   props.selectedCount          — GLOBAL selected seat count (all sections)
 *   props.maxQuantity            — per-order cap for the quantity strip
 *   props.onSetQuantity          — set cart quantity (null = multi ticket-type
 *                                  event; strip hidden, sidebar is source of truth)
 *   props.chipPriceCents         — ticket-type price used by the group chips
 *   props.totalAmountCents       — cart grand total (incl. passed-on fees)
 */
import React, { useState } from "react";
import {
  Armchair, RefreshCw, ArrowLeft, Sparkles, Loader2, Ticket, ChevronsUpDown, ChevronsDown, Info, AlertTriangle,
} from "lucide-react";
import VisualSeatMap from "./VisualSeatMap";
import QuantityStrip from "./QuantityStrip";
import GroupPriceChips from "./GroupPriceChips";
import { centsToAUD } from "../lib/events";
import WaitlistForm from "./WaitlistForm";
import CrossSellCard from "./CrossSellCard";

const STATUS_LABEL = {
  available: "Available",
  selling_fast: "Selling fast",
  almost_sold: "Almost sold",
  sold_out: "Sold out",
};

export default function InlineSectionSeatPanel({
  sectionName,
  sectionAvailability,
  zone,
  filteredSeats,
  layout,
  selectedSeats,
  onToggleSeat,
  totalTickets,
  onChangeSection,
  onBest4,
  best4Busy,
  onContinueCheckout,
  onRefresh,
  hideChangeSection = false,
  canContinueCheckout = false,
  // Sprint Y — sold-out waitlist
  eventSlug = null,
  waitlistEnabled = false,
  // Sprint AC — sold-out cross-sell to linked shows
  otherShows = [],
  eventId = null,
  // Sprint R1
  selectedCount = 0,
  maxQuantity = 0,
  onSetQuantity = null,
  chipPriceCents = 0,
  totalAmountCents = 0,
}) {
  const [collapsed, setCollapsed] = useState(false);
  if (!sectionName) return null;

  const status = sectionAvailability?.status || "available";
  const isSoldOut = status === "sold_out";
  const price = zone?.base_price_cents ?? sectionAvailability?.min_price_cents ?? 0;
  const availableCount = sectionAvailability?.available ?? 0;
  const heldCount = sectionAvailability?.held ?? 0;
  const soldCount = sectionAvailability?.sold ?? 0;
  const wheelchairCount = (filteredSeats || []).filter((s) => s.is_accessible).length;

  // Count seats picked in this section (part of the global selection)
  const idSet = new Set(selectedSeats || []);
  const picksInSection = (filteredSeats || []).filter((s) => idSet.has(s.id));
  const subtotal_cents = picksInSection.length * price;

  const seatsHaveCoords = (filteredSeats || []).some(
    (s) => typeof s.x_percent === "number" && typeof s.y_percent === "number",
  );

  return (
    <section
      className="mt-8 border border-[var(--gt-line)] rounded-sm bg-white overflow-hidden"
      aria-label={`Seats for section ${sectionName}`}
      data-testid="inline-section-seat-panel"
      data-section={sectionName}
    >
      {/* Header row */}
      <header className="px-5 py-4 border-b border-[var(--gt-line)] bg-[var(--gt-bg-surface)] flex flex-wrap items-start gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {zone?.hex_colour && (
            <span
              className="inline-block w-10 h-10 rounded-sm shrink-0 border border-black/10"
              style={{ backgroundColor: zone.hex_colour }}
              aria-hidden="true"
            />
          )}
          <div className="min-w-0">
            <div className="gt-label text-[10px] flex items-center gap-1.5">
              <Ticket size={11} /> Section
            </div>
            <h3
              className="font-heading text-2xl font-black tracking-tight truncate"
              data-testid="inline-section-name"
            >
              {sectionName}
            </h3>
            <div className="mt-1 flex items-center gap-3 text-[11px] uppercase tracking-widest text-[var(--gt-ink-3)] flex-wrap">
              <span className="font-mono font-bold text-[var(--gt-ink)]" data-testid="inline-section-price">
                {price > 0 ? `${centsToAUD(price)} AUD` : "Free"}
              </span>
              <span data-testid="inline-section-available-count">
                Available: <strong className="text-[var(--gt-ink)]">{availableCount}</strong>
              </span>
              <span data-testid="inline-section-picks-count">
                Selected: <strong className="text-[var(--gt-ink)]">{picksInSection.length}</strong>
              </span>
              <span data-testid="inline-selected-quantity">
                Qty: <strong className="text-[var(--gt-ink)]">{totalTickets}</strong>
              </span>
              {heldCount > 0 && <span>Held: {heldCount}</span>}
              {soldCount > 0 && <span>Sold: {soldCount}</span>}
              {wheelchairCount > 0 && (
                <span>Wheelchair: {wheelchairCount}</span>
              )}
              {status !== "available" && (
                <span
                  className={
                    "px-2 py-0.5 rounded-sm font-bold " +
                    (isSoldOut
                      ? "bg-neutral-200 text-neutral-700"
                      : status === "almost_sold"
                      ? "bg-orange-100 text-orange-800"
                      : "bg-amber-100 text-amber-800")
                  }
                  data-testid="inline-section-status-badge"
                >
                  {STATUS_LABEL[status]}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!hideChangeSection && (
            <button
              type="button"
              onClick={onChangeSection}
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-3 py-2 border border-[var(--gt-line)] rounded-sm bg-white hover:border-[var(--gt-ink)]"
              data-testid="inline-change-section-btn"
            >
              <ArrowLeft size={12} /> Change section
            </button>
          )}
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-3 py-2 border border-[var(--gt-line)] rounded-sm bg-white hover:border-[var(--gt-ink)]"
              data-testid="inline-refresh-availability-btn"
              aria-label="Refresh availability"
            >
              <RefreshCw size={12} /> Refresh
            </button>
          )}
          {!isSoldOut && (
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-3 py-2 border border-[var(--gt-line)] rounded-sm bg-white hover:border-[var(--gt-ink)]"
              data-testid="inline-toggle-seats-btn"
              aria-expanded={!collapsed}
            >
              {collapsed ? <ChevronsDown size={12} /> : <ChevronsUpDown size={12} />}
              {collapsed ? "Show seats" : "Hide seats"}
            </button>
          )}
        </div>
      </header>

      {/* Sold-out empty state */}
      {isSoldOut && (
        <div
          className="p-8 flex flex-col items-center justify-center text-center"
          data-testid="inline-section-sold-out"
        >
          <div className="w-14 h-14 rounded-full bg-neutral-100 flex items-center justify-center mb-3">
            <Armchair size={22} className="text-neutral-400" />
          </div>
          <p className="text-sm font-bold mb-1">This section is sold out.</p>
          <p className="text-xs text-[var(--gt-ink-3)] mb-4">Please choose another section.</p>
          {waitlistEnabled && eventSlug && (
            <div className="w-full max-w-md text-left mb-4" data-testid="section-waitlist-panel">
              <CrossSellCard otherShows={otherShows} sectionName={sectionName} sourceEventId={eventId} placement="section_panel" />
              <WaitlistForm slug={eventSlug} section={sectionName} compact />
            </div>
          )}
          {!hideChangeSection && (
            <button
              type="button"
              onClick={onChangeSection}
              className="gt-btn-secondary text-xs"
              data-testid="inline-change-section-btn-empty"
            >
              <ArrowLeft size={12} /> Change section
            </button>
          )}
        </div>
      )}

      {/* Seat map + subtotal */}
      {!isSoldOut && !collapsed && (
        <div className="p-5">
          {/* Sprint R1 — quantity strip + group price chips inside the panel */}
          {onSetQuantity && maxQuantity > 0 && (
            <div
              className="mb-5 pb-5 border-b border-[var(--gt-line)]"
              data-testid="inline-qty-picker"
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="gt-label">How many tickets?</span>
                <span className="font-mono font-bold text-sm" data-testid="inline-qty-value" aria-live="polite">
                  {totalTickets}
                </span>
              </div>
              <QuantityStrip
                value={totalTickets}
                max={maxQuantity}
                onChange={onSetQuantity}
                label="Ticket quantity"
                testId="inline-qty-strip"
              />
              <GroupPriceChips
                priceCents={chipPriceCents}
                max={maxQuantity}
                value={totalTickets}
                onPick={onSetQuantity}
                testIdPrefix="inline-group-chip"
              />
            </div>
          )}

          {seatsHaveCoords ? (
            <VisualSeatMap
              seats={filteredSeats}
              layout={layout}
              selectedSeats={selectedSeats}
              onToggleSeat={onToggleSeat}
              maxSelectable={totalTickets}
              onSwitchToList={null}
            />
          ) : (
            <div className="text-xs text-[var(--gt-ink-3)] p-4 border border-dashed border-[var(--gt-line)] rounded-sm" data-testid="inline-section-no-visual">
              <Info size={12} className="inline mr-1" />
              This section&apos;s seats aren&apos;t placed on the plan yet. Use the section list on the right to pick seats.
            </div>
          )}

          {/* Action row */}
          <div className="mt-5 pt-4 border-t border-[var(--gt-line)] flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-4 text-xs flex-wrap">
              <span className="gt-label">Subtotal for {sectionName}</span>
              <span
                className="font-mono font-bold text-base"
                data-testid="inline-section-subtotal"
              >
                {centsToAUD(subtotal_cents)}
              </span>
              <span className="gt-label border-l border-[var(--gt-line)] pl-4">Total (AUD)</span>
              <span
                className="font-mono font-bold text-base"
                data-testid="inline-total-amount"
              >
                {centsToAUD(totalAmountCents)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {totalTickets >= 4 && (
                <button
                  type="button"
                  onClick={onBest4}
                  disabled={best4Busy}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-3 py-2 border border-[var(--gt-ink)] rounded-sm bg-white text-[var(--gt-ink)] hover:bg-[var(--gt-bg-surface)] disabled:opacity-60"
                  data-testid="inline-best-4-together-btn"
                >
                  {best4Busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  Best 4 Together
                </button>
              )}
              <button
                type="button"
                onClick={onContinueCheckout}
                disabled={!canContinueCheckout}
                className="gt-btn-primary text-xs"
                data-testid="inline-continue-checkout-btn"
              >
                Continue checkout
              </button>
            </div>
          </div>

          {/* Sprint R1 — clear mismatch messaging under the action row */}
          {totalTickets === 0 ? (
            <div
              className="mt-3 flex items-start gap-1.5 text-[11px] font-semibold text-[var(--gt-ink-3)]"
              data-testid="inline-qty-zero-msg"
              role="status"
            >
              <Info size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>Set your ticket quantity to start picking seats.</span>
            </div>
          ) : selectedCount !== totalTickets ? (
            <div
              className="mt-3 flex items-start gap-1.5 text-[11px] font-semibold text-[var(--gt-accent)]"
              data-testid="inline-qty-mismatch-msg"
              role="status"
            >
              <AlertTriangle size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                {selectedCount < totalTickets
                  ? `Seats selected (${selectedCount}) don't match your ticket quantity (${totalTickets}) — pick ${totalTickets - selectedCount} more seat${totalTickets - selectedCount === 1 ? "" : "s"}.`
                  : `Seats selected (${selectedCount}) exceed your ticket quantity (${totalTickets}) — remove ${selectedCount - totalTickets} seat${selectedCount - totalTickets === 1 ? "" : "s"} or increase quantity.`}
              </span>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
